import _ from 'lodash';


/**
 * Sanitizes the provided HTML by removing potentially dangerous tags like <script>, <iframe>, <object>, and <embed>.
 *
 * @param {string} html - The HTML string to be sanitized.
 * @param {string} tagName - The tag name of the container element to wrap the HTML content (e.g., 'div', 'span').
 *
 * @returns {string} - The sanitized HTML, with dangerous tags removed.
 */
export function sanitizeHTML(html, tagName = 'p') {
  const tempDiv = document.createElement(tagName);
  tempDiv.innerHTML = html;

  // Remove <script> and other dangerous tags
  const scripts = tempDiv.querySelectorAll('script, iframe, object, embed');
  scripts.forEach(script => script.remove());

  // Return sanitized HTML
  return tempDiv.innerHTML;
}


/**
 * Converts a string label to a lowercase, underscore-separated format.
 *
 * Example: "Duties and taxes" → "duties_and_taxes"
 *
 * @param {string} label - The input string to be formatted.
 * @returns {string} The formatted string with lowercase letters and underscores instead of spaces.
 */
export function formatTypeLabel(label) {
  return label.toLowerCase().replace(/\s+/g, '_');
}



export function getDeepDifferences(obj1, obj2) {
  return _.transform(obj2, (result, value, key) => {
    if (!_.has(obj1, key)) {
      result[key] = value;
    } else if (_.isObject(value) && _.isObject(obj1[key])) {
      const diff = getDeepDifferences(obj1[key], value);
      if (!_.isEmpty(diff)) {
        result[key] = diff;
      }
    } else if (!_.isEqual(value, obj1[key])) {
      result[key] = value;
    }
  }, {});
}



/**
 * Converts a hex color to an RGB array.
 * @param {string} hex - The hex color string.
 * @returns {number[]} The RGB array.
 */
export function hexToRgb(hex) {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '');

  // Parse the r, g, b values
  let bigint;
  if (hex.length === 3) {
    bigint = parseInt(hex.split('').map(h => h + h).join(''), 16);
  } else {
    bigint = parseInt(hex, 16);
  }

  return [
    (bigint >> 16) & 255,
    (bigint >> 8) & 255,
    bigint & 255,
  ];
}



/**
 * Determines the appropriate text color (light or dark) based on the given background color.
 *
 * @param {string} color - The background color in rgba or hex format.
 * @param {string} [lightText='#fff'] - The light text color to use if the background is dark. Defaults to white.
 * @param {string} [darkText='#6c6c6c'] - The dark text color to use if the background is light. Defaults to dark gray.
 * @returns {string} The recommended text color.
 */
export function getTextColor(color, lightText = '#fff', darkText = '#6c6c6c') {
  if (!!!color) {
    return undefined;
  }

  let rgbaValues;

  if (color.startsWith('#')) {
    rgbaValues = hexToRgb(color);
  } else {
    rgbaValues = color.match(/\d+/g).map(Number);
  }

  const brightness = (rgbaValues[0] * 0.299) + (rgbaValues[1] * 0.587) + (rgbaValues[2] * 0.114);

  return brightness > 186 ? darkText : lightText;
}

